/**
 * Three.js カルーセル
 *
 * 構成
 * ├─ tiltGroup   : カルーセル全体の傾き
 * └─ rotateGroup : カルーセルの回転
 *
 * スライド状態
 * active : 正面
 * prev   : 左側
 * next   : 右側
 * other  : その他
 */

import * as THREE from 'three';

import { IMAGE_COUNT, CONFIG } from './config.js';
import { createSlideGeometry, createSlides, setSlidePosition } from './createSlides.js';
import { updateSlideStates, getActiveIndex } from './updateSlideStates.js';
import { updateAutoRotation } from './updateAutoRotation.js';

const init = () => {
	// =========================
	// 初期設定
	// =========================
	// DOMの存在確認
	const canvasEl = document.querySelector('[data-carousel]');
	if (!canvasEl) {
		return;
	}
	// スライド枚数の確認
	if (IMAGE_COUNT < 3) {
		return;
	}

	// =========================
	// Scene
	// =========================
	const width = canvasEl.clientWidth;
	const height = canvasEl.clientHeight;
	const scene = new THREE.Scene();
	// カメラの設定
	const camera =
		new THREE.PerspectiveCamera(
			window.innerWidth >= CONFIG.breakpoint
				? CONFIG.cameraFovPc
				: CONFIG.cameraFovSp,   // 視野角
			width / height,         // アスペクト比
			0.1,                    // near
			1000                    // far
		);
	// カメラの座標指定と視野角
	if (window.innerWidth >= CONFIG.breakpoint) {
		camera.position.z = CONFIG.cameraPositionZPc;
	} else {
		camera.position.z = CONFIG.cameraPositionZSp;
	}
	// レンダラーを作成
	const renderer =
		new THREE.WebGLRenderer({
			antialias: true, // 輪郭のギザギザ（ジャギー）を滑らかにする
			alpha: true  // キャンバスの背景色を透明（アルファチャンネル）にする
		});

	// 色味補正
	renderer.outputColorSpace = THREE.SRGBColorSpace;
	renderer.toneMapping = THREE.NoToneMapping;

	renderer.setPixelRatio(
		Math.min(window.devicePixelRatio, 2)
	);
	// 描画サイズ
	renderer.setSize(
		width,
		height
	);
	// レンダラーのcanvasを追加
	canvasEl.appendChild(
		renderer.domElement
	);

	// =========================
	// Group
	// =========================
	// カルーセル全体の傾きグループ
	const tiltGroup = new THREE.Group();
	scene.add(tiltGroup);
	// カルーセルの回転グループ
	const rotateGroup = new THREE.Group();
	tiltGroup.add(rotateGroup);
	tiltGroup.position.y = 0.2;
	// カルーセル全体の傾き指定
	tiltGroup.rotation.x =
		THREE.MathUtils.degToRad(
			CONFIG.tiltDegreeX
		);
	tiltGroup.rotation.y =
		THREE.MathUtils.degToRad(
			CONFIG.tiltDegreeY
		);
	tiltGroup.rotation.z =
		THREE.MathUtils.degToRad(
			CONFIG.tiltDegreeZ
		);

	// =========================
	// Slide作成
	// =========================
	const textureLoader = new THREE.TextureLoader();
	createSlides({
		rotateGroup,
		textureLoader,
	});

	// =========================
	// Raycaster
	// =========================
	// クリック判定を入れるためレイキャストを作成
	const raycaster = new THREE.Raycaster();
	// マウス座標管理用に2次元ベクトルを作成
	const mouse = new THREE.Vector2();

	// =========================
	// 回転制御
	// =========================
	// スライド1枚あたりの角度を取得
	const step = (Math.PI * 2) / IMAGE_COUNT;
	// 現在の回転角度（最初のactive位置補正）
	const offset = step * 0.2;
	let rotationY = offset;
	// 最終的に到達したい回転角度
	let targetRotation = rotationY;
	// 停止時間管理用
	let pauseUntil = 0;
	// スライド切替え中か判定用のフラグ
	let isAnimating = false;
	let moveTargetRotation = null;
	// 停止状態を判定
	let isAutoPlaying = true;

	/**
	 * 指定スライドを正面へ移動
	 * @param {number} targetIndex
	 */
	const moveToSlide = (targetIndex, direction) => {
		// アニメーション中の場合はクリックしても回転しない
		if (isAnimating) {
			return;
		}
		// テキストスクランブル中はクリックしても回転しない
		const animeTargetList = document.querySelectorAll('[data-scramble-anime-target]');
		const isScramble =
			[...animeTargetList].some(
				element =>
					element.dataset.scrambling === 'true'
			);
		if (isScramble) {
			return;
		}
		// 現在の正面スライドが正面位置からどれだけズレているか取得
		// 現在の rotationY から最も近い正面角度を取得
		const currentFrontRotation =
			Math.round(
				(rotationY - offset) /
				step
			) *
			step +
			offset;
		// 移動方向
		const move =
			direction === 'next'
				? -step
				: step;
		// 目標角度を算出
		targetRotation = currentFrontRotation + move;
		// 手動移動用の目標角度
		moveTargetRotation = targetRotation;
		// スライド移動中にする
		isAnimating = true;
		// 自動回転を一時的に停止
		pauseUntil = Date.now() + CONFIG.pauseDuration;
	};
	// =========================
	// スライド外観制御
	// =========================
	/**
	 * アニメーションを滑らかにする
	 * @param {number} current 
	 * @param {number} target 
	 * @param {number} speed 
	 * @returns 
	 */
	const lerpValue = (
		current,
		target,
		speed = 0.1 // 1フレームに10%分移動する
	) => {
		return current +
			(target - current) *
			speed;
	};
	/**
	 * スライドの見た目だけを更新
	 */
	const updateSlideAppearance = () => {
		rotateGroup.children.forEach(mesh => {
			// スライド移動時でも縦横比を維持したまま拡大・縮小
			mesh.scale.x = lerpValue(
				mesh.scale.x,
				1
			);
			mesh.scale.y = lerpValue(
				mesh.scale.y,
				1
			);
		});
	};
	// =========================
	// Click・Hover
	// =========================
	/**
	 * マウス位置にあるスライドを取得
	 * @param {MouseEvent} event
	 * @returns {THREE.Mesh|null}
	 */
	const getIntersectedMesh = event => {
		// Canvasの位置を取得
		const rect = renderer.domElement.getBoundingClientRect();
		// マウス座標をThree.js用へ変換
		mouse.x = (event.clientX - rect.left) / rect.width * 2 - 1;
		mouse.y = -(event.clientY - rect.top) / rect.height * 2 + 1;
		// Raycasterへセット
		raycaster.setFromCamera(mouse, camera);
		// 当たったMeshを取得
		const intersects = raycaster.intersectObjects(rotateGroup.children);
		if (!intersects.length) {
			return null;
		}
		// 当たった一番手前のmeshだけ返す
		const mesh = intersects.find(item => {
			return (
				item.object.userData.state === 'prev' ||
				item.object.userData.state === 'next' ||
				item.object.userData.state === 'active'
			);
		});
		return mesh?.object ?? null;
	};
	// 正面のスライドをクリックしたら指定のurlへ遷移
	renderer.domElement.addEventListener('click', event => {
		moveTargetRotation = targetRotation;
		// 回転中はクリック無効
		if (isAnimating) {
			return;
		}
		// クリックしたスライドを取得
		const mesh = getIntersectedMesh(event);
		if (!mesh) {
			return;
		}
		// 指定のurlへ遷移
		else if (mesh.userData.state === 'active') {
			window.location.href = mesh.userData.url;
			return;
		}
		// SPの場合は左右のスライドタップに反応させる
		else if (
			window.innerWidth < CONFIG.breakpoint &&
			(
				mesh.userData.state === 'prev' ||
				mesh.userData.state === 'next'
			)
		) {
			moveToSlide(mesh.userData.index, mesh.userData.state);
		}
	});
	// 各ボタンのDOMを取得
	const prevButton = document.querySelector('[data-carousel-prev]');
	const nextButton = document.querySelector('[data-carousel-next]');
	const pauseButton = document.querySelector('[data-carousel-pause]');
	const pauseButtonText = document.querySelector('[data-carousel-pause_txt]');
	if (
		!prevButton ||
		!nextButton ||
		!pauseButton ||
		!pauseButtonText
	) {
		return;
	}
	// 戻るボタンで前のスライドを正面へ
	prevButton.addEventListener('click', () => {
		if (isAnimating) {
			return;
		}
		const activeIndex = getActiveIndex(rotationY);
		const prevIndex =
			(
				activeIndex -
				1 +
				IMAGE_COUNT
			) % IMAGE_COUNT;
		moveToSlide(prevIndex, 'prev');
	});
	// 次へボタンで前のスライドを正面へ
	nextButton.addEventListener('click', () => {
		if (isAnimating) {
			return;
		}
		const activeIndex = getActiveIndex(rotationY);
		const nextIndex =
			(
				activeIndex +
				1
			) % IMAGE_COUNT;
		moveToSlide(nextIndex, 'next');
	});
	// 自動回転停止⇔再生ボタン
	pauseButton.addEventListener('click', () => {
		isAutoPlaying = !isAutoPlaying;
		if (isAutoPlaying) {
			pauseButtonText.classList.remove('is-pausing');
			pauseButton.setAttribute(
				'aria-label',
				'自動回転を停止'
			);
		} else {
			pauseButtonText.classList.add('is-pausing');
			pauseButton.setAttribute(
				'aria-label',
				'自動回転を開始'
			);
		}
		animate();
	});
	// ホバー判定
	renderer.domElement.addEventListener('mousemove', event => {
		// ホバーしたスライドを取得
		const mesh = getIntersectedMesh(event);
		// 正面のスライドは「cursor:pointer;」とする
		renderer.domElement.style.cursor =
			mesh?.userData.state === 'active'
				? 'pointer'
				: 'default';
	});
	// Canvasから出たらcursorのstyleをdefaultにする
	renderer.domElement.addEventListener('mouseleave', () => {
		renderer.domElement.style.cursor =
			'default';
	});

	// =========================
	// Animation
	// =========================
	const clock = new THREE.Clock();

	const animate = () => {
		requestAnimationFrame(animate);

		const delta = Math.min(
			clock.getDelta(),
			0.05
		);
		// 追従
		let leapSpeed;

		if (window.innerWidth >= CONFIG.breakpoint) {
			leapSpeed =
				CONFIG.rotationLeapSpeedPc[IMAGE_COUNT];
		} else {
			leapSpeed =
				CONFIG.rotationLeapSpeedSp[IMAGE_COUNT];
		}
		const factor =
			1 -
			Math.pow(
				1 - leapSpeed,
				delta * 60
			);
		// 手動移動
		if (moveTargetRotation !== null) {
			rotationY +=
				(
					moveTargetRotation -
					rotationY
				) * factor;

			// 手動移動完了
			if (
				Math.abs(
					moveTargetRotation -
					rotationY
				) < 0.001
			) {
				rotationY = moveTargetRotation;
				moveTargetRotation = null;
				isAnimating = false;
			}
		}
		if (isAutoPlaying && moveTargetRotation === null) {
			// 自動回転
			targetRotation =
				updateAutoRotation({
					step,
					offset,
					targetRotation,
					rotationY,
					pauseUntil,
					delta,
					isAutoPlaying,
				});
		}
		rotationY +=
			(
				targetRotation -
				rotationY
			) * factor;

		rotateGroup.rotation.y = rotationY;
		updateSlideStates({
			rotateGroup,
			rotationY,
		});
		updateSlideAppearance();
		renderer.render(scene, camera);
	};

	// =========================
	// Animation Start
	// =========================
	const startAnimation = () => {
		updateSlideStates({
			rotateGroup,
			rotationY,
		});
		updateSlideAppearance();
		renderer.render(scene, camera);
		requestAnimationFrame(animate);
	};

	// =========================
	// Loading
	// =========================
	if (
		document.documentElement.classList.contains(
			'is-finishLoad'
		)
	) {
		startAnimation();
	} else {
		document.addEventListener(
			'loadingComplete',
			startAnimation,
			{ once: true }
		);
	}

	// =========================
	// Resize
	// =========================
	let timeoutId = null;
	// 負荷軽減のためデバウンス処理を入れる
	window.addEventListener('resize', () => {
		if (timeoutId) {
			clearTimeout(timeoutId)
		};
		timeoutId = setTimeout(() => {
			// スライド用Geometry再生成
			rotateGroup.children.forEach((mesh, index) => {
				mesh.geometry.dispose();
				mesh.geometry = createSlideGeometry();
				setSlidePosition({ mesh, index });
			});
			const width = canvasEl.clientWidth;
			const height = canvasEl.clientHeight;
			camera.aspect = width / height;
			// 見た目更新
			updateSlideAppearance();

			// カメラの座標と視野角更新
			if (window.innerWidth >= CONFIG.breakpoint) {
				camera.position.z = CONFIG.cameraPositionZPc;
				camera.fov = CONFIG.cameraFovPc;
			} else {
				camera.position.z = CONFIG.cameraPositionZSp;
				camera.fov = CONFIG.cameraFovSp;
			}
			camera.updateProjectionMatrix();
			renderer.setPixelRatio(
				Math.min(window.devicePixelRatio, 2)
			);
			renderer.setSize(
				width,
				height
			);
		}, 200);
	});
};


// ============================================
// 実行
// ============================================
if (document.readyState === 'loading') {
	document.addEventListener(
		'DOMContentLoaded',
		init,
		{ once: true }
	);
} else {
	init();
}
